import { useLazyQuery } from '@apollo/client';
import { logger } from '@monofront/logging';
import {
  ICustomerAddress,
  IShoppingCartContext,
  IUseLocalStorage,
  ListEligibleShippingMethodsQueryResult,
  PreferredShippingMethod,
  ShippingMethod,
  ShippingMethodTypes,
  ShoppingCartProductType,
} from '@monofront/types';
import { createActorContext } from '@xstate/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ActorOptions, fromPromise } from 'xstate';
import {
  getIsShippingFreshMethodEligible,
  getIsShippingUltraFreshMethodEligible,
} from '../hooks/useDeliveryMethodEligibility';
import { ICustomerCRUD } from '../interfaces/ICustomerCRUD';
import { LIST_ELIGIBLE_SHIPPING_METHODS } from '../queries';
import {
  DeliveryBarMachineAddress,
  DeliveryBarMachineContextShippingMethod,
  machine,
  STATES_ENUM,
} from '../stateMachines/deliveryBar';
import { DELIVERY_BAR_EXTERNAL_SERVICES } from '../stateMachines/deliveryBar/services';
import { areObjectsEqual } from '../utils/object';
import { IAuthState } from './createAuthContext';
import { validateDeliveryBarState } from './deliveryBar/deliveryBarFormValidator';
import { useEligibility } from './EligibilityContext';

export type DeliveryBarAddressFormContent = {
  slices: any;
};

export type DeliveryBarShippingMethodSelectionContent = {
  slices: any;
  slices1: any;
  shippingMethods: any;
};

export type DeliveryBarContent =
  | Record<string, never>
  | {
      addressFormContent: DeliveryBarAddressFormContent;
      shippingMethodSelectionContent: DeliveryBarShippingMethodSelectionContent;
      waitingProduct?: ShoppingCartProductType;
      setWaitingProduct: (waitingProduct?: ShoppingCartProductType) => void;
    };

const DeliveryBarContext = createContext<DeliveryBarContent>({});

const DeliveryBarMachineContext = createActorContext(machine);

type CreateDeliveryBarContextProps = {
  useAuthentication: () => IAuthState;
  useLocalStorage: IUseLocalStorage;
  useShoppingCart: () => IShoppingCartContext;
  customerCRUD: ICustomerCRUD;
};

export type DeliverBarContextProviderProps = React.PropsWithChildren<{
  content: DeliveryBarContent;
  machineConfigOptions?: ActorOptions<typeof machine>;
  actionsCallbacks?: {
    [serviceName: string]: {
      success?: () => void;
      error?: () => void;
    };
  };
}>;

export enum ErrorTypes {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  INELIGIBLE_PRODUCTS_IN_CART = 'INELIGIBLE_PRODUCTS_IN_CART',
  INELIGIBLE_PRODUCT_ADDED_TO_CART = 'INELIGIBLE_PRODUCT_ADDED_TO_CART',
  INELIGIBLE_SHIPPING_METHOD_SELECTED = 'INELIGIBLE_SHIPPING_METHOD_SELECTED',
}

type CustomerPayload = {
  address?: ICustomerAddress;
  preferredShippingMethod?: PreferredShippingMethod;
};

export const formatShippingMethod = (
  shippingMethod: PreferredShippingMethod,
): DeliveryBarMachineContextShippingMethod => {
  switch (shippingMethod.type) {
    case ShippingMethodTypes.PICKUP:
      return {
        type: shippingMethod.type,
        carrierId: shippingMethod.carrierId ?? undefined,
        pickupPointId: shippingMethod.pickupPointId ?? null,
        pickupPointCity: shippingMethod.pickupPointCity ?? null,
        pickupPointLabel: shippingMethod.pickupPointLabel ?? null,
        pickupPointStreet: shippingMethod.pickupPointStreet ?? null,
        pickupPointZipCode: shippingMethod.pickupPointZipCode ?? null,
      };

    case ShippingMethodTypes.COURIER:
    case ShippingMethodTypes.HOME:
    case ShippingMethodTypes.CLICK_AND_COLLECT:
    default:
      return {
        type: shippingMethod.type,
        carrierId: shippingMethod.carrierId ?? undefined,
      };
  }
};

const formatCustomerData = ({
  address,
  shippingMethod,
}: {
  address: DeliveryBarMachineAddress;
  shippingMethod: DeliveryBarMachineContextShippingMethod;
}): CustomerPayload => {
  let preferredShippingMethod:
    | CustomerPayload['preferredShippingMethod']
    | undefined = undefined;

  if (shippingMethod) {
    const { type, carrierId } = shippingMethod;

    switch (type) {
      case ShippingMethodTypes.PICKUP:
        preferredShippingMethod = {
          type,
          carrierId: carrierId ?? null,
          pickupPointId: shippingMethod.pickupPointId ?? null,
          pickupPointCity: shippingMethod.pickupPointCity ?? null,
          pickupPointStreet: shippingMethod.pickupPointStreet ?? null,
          pickupPointZipCode: shippingMethod.pickupPointZipCode ?? null,
          pickupPointLabel: shippingMethod.pickupPointLabel ?? null,
        };
        break;

      case ShippingMethodTypes.COURIER:
      case ShippingMethodTypes.HOME:
      case ShippingMethodTypes.CLICK_AND_COLLECT:
        preferredShippingMethod = {
          type,
          carrierId: carrierId ?? null,
        };
        break;
    }
  }

  return {
    address: {
      street: address.street,
      city: address.city,
      postalCode: address.postalCode,
      country: address.country,
    },
    preferredShippingMethod,
  };
};

const createDeliveryBarContext = ({
  useAuthentication,
  useLocalStorage,
  useShoppingCart,
  customerCRUD,
}: CreateDeliveryBarContextProps) => {
  const DeliverBarContextProvider: React.FC<DeliverBarContextProviderProps> = ({
    children,
    content = {},
    machineConfigOptions = {},
    actionsCallbacks = {},
  }) => {
    const [waitingProduct, setWaitingProduct] = useState<
      ShoppingCartProductType | undefined
    >(undefined);
    const { shoppingCart } = useShoppingCart();

    const {
      resolvedCustomer: customer,
      localCustomer,
      remoteCustomer: authenticatedCustomer,
      setStorageValue,
      clearLocalStorage,
    } = useLocalOrRemoteCustomer({
      useAuthentication,
      useLocalStorage,
    });

    const { checkEligibility, address: eligibilityTestedAddress } =
      useEligibility();

    const [listEligibleShippingMethods] =
      useLazyQuery<ListEligibleShippingMethodsQueryResult>(
        LIST_ELIGIBLE_SHIPPING_METHODS,
        {
          ssr: false,
          fetchPolicy: 'cache-and-network',
        },
      );

    // Reset machine to initial state when side panel closes
    const saveCustomerData = useCallback(
      async ({ data }: { data: CustomerPayload }) => {
        if (authenticatedCustomer?.uid) {
          try {
            await customerCRUD.update(authenticatedCustomer.uid, {
              address: data.address,
              preferredShippingMethod: data.preferredShippingMethod,
            });

            clearLocalStorage();

            return;
          } catch (error) {
            logger.error('Failed to update customer data:', error);
          }
        } else {
          setStorageValue(data);
        }

        return;
      },
      [authenticatedCustomer, setStorageValue, customerCRUD, clearLocalStorage],
    );

    useEffect(() => {
      if (authenticatedCustomer?.uid && localCustomer) {
        // save local customer data to remote if user is authenticated and local data exists
        void saveCustomerData({ data: localCustomer });
      }
    }, [authenticatedCustomer, localCustomer, saveCustomerData]);

    const input = useMemo(
      () => ({
        address: customer?.address || null,
        shippingMethod: customer?.preferredShippingMethod
          ? formatShippingMethod(customer.preferredShippingMethod)
          : null,
        eligibleShippingMethods: [],
        callbacks: actionsCallbacks,
      }),
      [customer, actionsCallbacks],
    );

    const actors = useMemo(
      () => ({
        [DELIVERY_BAR_EXTERNAL_SERVICES.fetchShippingMethods]: fromPromise<
          ShippingMethod[],
          {
            address: DeliveryBarMachineAddress;
          }
        >(async ({ input }) => {
          const { data, error } = await listEligibleShippingMethods({
            variables: {
              shippingAddress: {
                countryCode: input.address.country,
                postcode: input.address.postalCode,
              },
            },
          });

          if (error) {
            logger.error('Failed to fetch shipping methods:', error);

            return [];
          }

          return data?.listEligibleShippingMethods ?? [];
        }),

        [DELIVERY_BAR_EXTERNAL_SERVICES.updateCustomerData]: fromPromise<
          void,
          {
            address: DeliveryBarMachineAddress;
            shippingMethod: DeliveryBarMachineContextShippingMethod;
          }
        >(async ({ input }) => {
          const data = formatCustomerData({
            address: input.address,
            shippingMethod: input.shippingMethod,
          });

          await saveCustomerData({ data });
        }),
        [DELIVERY_BAR_EXTERNAL_SERVICES.checkAddressEligibility]: fromPromise<
          void,
          {
            address: DeliveryBarMachineAddress;
          }
        >(async ({ input }) => {
          if (!input.address) return;
          const { city, country, postalCode, street } = input.address;

          const needToCheckEligibility =
            !eligibilityTestedAddress ||
            !areObjectsEqual(
              {
                city: eligibilityTestedAddress.city,
                country: eligibilityTestedAddress.country,
                postalCode: eligibilityTestedAddress.postalCode,
                street: eligibilityTestedAddress.street,
              },
              { city, country, postalCode, street },
            );

          if (needToCheckEligibility) {
            checkEligibility(input.address);
          }
        }),

        [DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData]: fromPromise<
          void,
          {
            address: DeliveryBarMachineAddress;
            shippingMethod: DeliveryBarMachineContextShippingMethod;
          }
        >(async ({ input }) => {
          const deliveryBarState = validateDeliveryBarState({
            selectedShippingMethod: input.shippingMethod,
            shoppingCart,
            isShippingFreshMethodEligible: getIsShippingFreshMethodEligible(
              input.shippingMethod,
            ),
            isShippingUltraFreshMethodEligible:
              getIsShippingUltraFreshMethodEligible(input.shippingMethod),
            waitingProduct,
          });

          if (!deliveryBarState.isValid) {
            throw new Error('Form validation failed');
          }
        }),
      }),
      [
        listEligibleShippingMethods,
        saveCustomerData,
        shoppingCart,
        eligibilityTestedAddress,
        checkEligibility,
        waitingProduct,
      ],
    );

    return (
      <DeliveryBarContext.Provider
        value={{
          addressFormContent: content.addressFormContent,
          shippingMethodSelectionContent:
            content.shippingMethodSelectionContent,
          waitingProduct,
          setWaitingProduct,
        }}
      >
        <DeliveryBarMachineContext.Provider
          options={{
            ...machineConfigOptions,
            input,
          }}
          logic={machine.provide({ actors })}
        >
          {children}
        </DeliveryBarMachineContext.Provider>
      </DeliveryBarContext.Provider>
    );
  };

  return {
    DeliverBarContextProvider,
    useDeliveryBar: () => useContext(DeliveryBarContext),
  };
};

const DEFAULT_LOCAL_STORAGE_STATE = null;
const useLocalOrRemoteCustomer = ({
  useAuthentication,
  useLocalStorage,
}: {
  useAuthentication: () => IAuthState;
  useLocalStorage: IUseLocalStorage;
}) => {
  const { customer, isCustomerLoading } = useAuthentication();
  const [storageValue, setStorageValue] =
    useLocalStorage<CustomerPayload | null>(
      'lf_unauthenticated_customer',
      DEFAULT_LOCAL_STORAGE_STATE,
    );

  const clearLocalStorage = useCallback(
    () => setStorageValue(DEFAULT_LOCAL_STORAGE_STATE),
    [setStorageValue],
  );

  return {
    localCustomer: storageValue,
    remoteCustomer: customer,
    resolvedCustomer: storageValue ?? customer,
    setStorageValue,
    clearLocalStorage,
    isCustomerLoading: storageValue ? false : isCustomerLoading,
  };
};

export {
  createDeliveryBarContext,
  DeliveryBarContext,
  DeliveryBarMachineContext,
  STATES_ENUM,
  useLocalOrRemoteCustomer,
};
